/* eslint-disable semi, no-await-in-loop, no-restricted-syntax, no-restricted-globals, no-underscore-dangle, no-plusplus */
/* ============================================================================
 * QA automation bridge — lets the admin panel's "Testing" page drive the REAL
 * app UI like a human (login, etc.), entirely client-side, with no extra server.
 *
 * How it works: the admin Testing page embeds this app in an iframe at
 *   https://projector.app/newSign-in?qa_bridge=1
 * This module (only when ?qa_bridge=1 is present) performs the real on-screen
 * actions — typing into inputs, clicking buttons — inside THIS app's own page
 * (same-origin to itself, so it's allowed), and reports each step back to the
 * admin page via postMessage. Turnstile passes because it's a real user browser.
 *
 * SAFETY:
 *  - Does nothing unless the URL has ?qa_bridge=1.
 *  - Only obeys messages from an allow-listed admin origin.
 *  - Holds NO credentials: the admin page supplies email/password per run.
 *  - Only performs actions a human could already do on the form; it never reads
 *    out tokens/cookies or exposes anything the page doesn't already show.
 * ========================================================================== */

// Deploy marker — on ANY load of the app, `window.__pjQaBridge` becomes 'loaded'.
// Quick check that a build WITH the bridge is actually live: open the app and
// run `window.__pjQaBridge` in the console (undefined = old bundle still cached).
try { window.__pjQaBridge = 'loaded' } catch { /* noop */ }

const ALLOWED_ORIGINS = [
  'http://localhost:5173',
  'http://localhost:4173',
  'http://127.0.0.1:5173',
  'https://admin-projector.eq7.in',
  'https://admin.projectorstream.com',
]
// Any localhost origin is allowed too, so local dev (admin on :5173, app on
// :3000) works without deploying and regardless of the admin's dev port.
const LOCAL_ORIGIN = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/
function isAllowedOrigin(origin) {
  return ALLOWED_ORIGINS.includes(origin) || LOCAL_ORIGIN.test(origin)
}

/* ---------- tiny DOM helpers (human-like) ---------- */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms))
const isVisible = (el) => !!el && !!el.offsetParent && el.getClientRects().length > 0

async function waitFor(getter, { timeout = 15000, interval = 200 } = {}) {
  const end = Date.now() + timeout
  for (;;) {
    let v
    try { v = getter() } catch { v = null }
    if (v) return v
    if (Date.now() >= end) throw new Error('timeout')
    await sleep(interval)
  }
}

function visibleByPlaceholder(ph) {
  return Array.from(document.querySelectorAll(`input[placeholder="${ph}"]`)).find(isVisible) || null
}

// The visible password field — by type, since its placeholder is localized
// (signInText.enterYourPassword) and not a fixed string. Skips the hidden
// name="password" mirror (which isn't visible) and the email field.
function passwordInput() {
  const byType = Array.from(document.querySelectorAll('input[type="password"]')).find(isVisible)
  if (byType) return byType
  return Array.from(document.querySelectorAll('input')).find(
    (el) => isVisible(el) && el.type !== 'email'
      && /password/i.test(`${el.placeholder || ''} ${el.getAttribute('aria-label') || ''} ${el.id || ''}`),
  ) || null
}
// A visible, truly-enabled button whose label matches `reInclude` (and not
// `reExclude`). Handles both the `disabled` property and `aria-disabled`.
function buttonLabel(b) {
  return `${b.textContent || ''} ${b.value || ''} ${b.getAttribute('aria-label') || ''}`.trim()
}
const isEnabled = (b) => isVisible(b) && !b.disabled && b.getAttribute('aria-disabled') !== 'true'

// The sign-in form's primary CTA (email "Continue" and password "Continue" both
// carry styles.primaryAction; CRA keeps that name in the hashed class). This is
// the ONE button we ever click — never the Google/Apple social buttons, which
// live in a different container and whose label happens to contain "Continue".
function submitButton() {
  const byClass = Array.from(document.querySelectorAll('[class*="primaryAction"]')).find(isEnabled)
  if (byClass) return byClass
  // Fallback if the module class ever changes: an enabled Continue/Get Code/
  // Signing-In button that is explicitly NOT a social ("…with Google/Apple").
  return Array.from(document.querySelectorAll('button, input[type="submit"], [role="button"]')).find((b) => {
    if (!isEnabled(b)) return false
    const t = buttonLabel(b)
    if (/google|apple|\bwith\b|sign ?up/i.test(t)) return false
    return /^(continue|get code|signing in)/i.test(t)
  }) || null
}
function visibleButtonsDump() {
  return Array.from(document.querySelectorAll('button, input[type="submit"], input[type="button"], [role="button"]'))
    .filter(isVisible)
    .map((b) => `"${buttonLabel(b).slice(0, 24)}"${b.disabled ? '(disabled)' : ''}`)
    .slice(0, 10)
    .join(', ')
}

function pressEnter(el) {
  if (!el) return
  el.focus()
  const opts = { key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true }
  el.dispatchEvent(new KeyboardEvent('keydown', opts))
  el.dispatchEvent(new KeyboardEvent('keyup', opts))
}
function pageHasText(re) {
  return re.test(document.body ? document.body.innerText : '')
}

// React tracks the input's value internally; set it via the native setter and
// dispatch a real input event so React's onChange fires.
function setNativeValue(el, value) {
  const proto = el instanceof window.HTMLTextAreaElement ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype
  const setter = Object.getOwnPropertyDescriptor(proto, 'value').set
  setter.call(el, value)
  el.dispatchEvent(new Event('input', { bubbles: true }))
}
async function humanType(el, text) {
  el.focus()
  setNativeValue(el, '')
  let acc = ''
  for (const ch of String(text)) {
    acc += ch
    setNativeValue(el, acc)
    await sleep(35)
  }
  el.dispatchEvent(new Event('change', { bubbles: true }))
  if (typeof el.blur === 'function') el.blur()
}

function requireField(v, name) {
  if (!v) throw new Error(`No ${name} supplied by the admin page.`)
  return v
}

// Start every QA run from a logged-out session so the sign-in form actually
// shows (otherwise a token left over from a previous run keeps us "logged in").
// Targeted: only auth-ish keys, so we don't wipe unrelated state. Runs at init,
// before React reads the token, so the app boots signed out.
function clearAuthForFreshSession() {
  const EXACT = ['token', 'uploader_token', 'uploader_token_expiry', 'user_uuid']
  try {
    const ls = window.localStorage
    for (const k of EXACT) ls.removeItem(k)
    for (const k of Object.keys(ls)) {
      if (/token|auth|session|logged|uuid/i.test(k)) ls.removeItem(k)
    }
  } catch { /* storage blocked */ }
  try { window.sessionStorage.clear() } catch { /* noop */ }
}

/* ---------- flows ---------- */
// Each step: { title, hint, run(cfg) } — returns a detail string or throws.
const FLOWS = {
  login: {
    title: 'Login',
    steps: [
      {
        title: 'Open sign-in page',
        hint: 'wait for form',
        run: async () => {
          await waitFor(() => pageHasText(/Welcome back\. Sign in to continue to your Projector\./) || visibleByPlaceholder('Email'), { timeout: 25000 })
          return `loaded ${location.pathname}`
        },
      },
      {
        title: 'Enter email',
        hint: 'type email · existence probe',
        run: async (cfg) => {
          const email = requireField(cfg.email, 'email')
          const input = await waitFor(() => visibleByPlaceholder('Email'), { timeout: 15000 })
          await humanType(input, email)
          return email
        },
      },
      {
        title: 'Pass bot check',
        hint: 'Cloudflare Turnstile → Continue',
        run: async () => {
          const passwordShown = () => passwordInput()
          let btn
          try {
            btn = await waitFor(submitButton, { timeout: 35000 })
          } catch {
            throw new Error(`The email "Continue" button never enabled (Turnstile / email probe). Visible buttons: ${visibleButtonsDump() || 'none'}`)
          }
          // Click; if the password step doesn't appear, retry and fall back to Enter.
          for (let attempt = 0; attempt < 3; attempt++) {
            const b = submitButton() || btn
            if (typeof b.focus === 'function') b.focus()
            b.click()
            try {
              await waitFor(passwordShown, { timeout: 6000 })
              return 'continued to password'
            } catch {
              pressEnter(visibleByPlaceholder('Email'))
            }
            try {
              await waitFor(passwordShown, { timeout: 4000 })
              return 'continued to password'
            } catch {
              /* retry the loop */
            }
          }
          throw new Error(`Clicked Continue, but the password field never appeared. Visible buttons: ${visibleButtonsDump() || 'none'}`)
        },
      },
      {
        title: 'Enter password',
        hint: 'type password',
        run: async (cfg) => {
          const pw = requireField(cfg.password, 'password')
          const input = await waitFor(passwordInput, { timeout: 10000 })
          await humanType(input, pw)
          return 'password entered'
        },
      },
      {
        title: 'Sign in',
        hint: 'submit → /v4/signin',
        run: async () => {
          const btn = await waitFor(submitButton, { timeout: 12000 })
          if (typeof btn.focus === 'function') btn.focus()
          btn.click()
          return 'submitted'
        },
      },
      {
        title: 'Verify signed in',
        hint: 'land on /access',
        run: async () => {
          const end = Date.now() + 25000
          while (Date.now() < end) {
            const path = location.pathname
            if (/\/access|\/quick-user-payment/.test(path)) return `signed in → ${path}`
            if (/newMobile-verification|newVerification-code|email-verification/.test(path)) {
              throw new Error(`Hit a verification wall (${path}) — use a QA account without MFA / with a verified email.`)
            }
            if (pageHasText(/Incorrect password|Oops! Something went wrong|couldn't verify your email/i)) {
              throw new Error('Login rejected — check the credentials.')
            }
            if (pageHasText(/Ready to watch new content added by your projectors/i)) return 'signed in (welcome shown)'
            await sleep(500)
          }
          throw new Error(`No success signal within 25s (still on ${location.pathname}).`)
        },
      },
    ],
  },
}

/* ---------- controller messaging ---------- */
export function initQaBridge() {
  let params
  try { params = new URLSearchParams(window.location.search) } catch { return }
  if (params.get('qa_bridge') !== '1') return

  try { window.__pjQaBridge = 'active' } catch { /* noop */ }
  // eslint-disable-next-line no-console
  console.info('[qa-bridge] active on', window.location.pathname)

  // Fresh session every run — before React boots and reads the token.
  clearAuthForFreshSession()

  const controller = window.opener && window.opener !== window ? window.opener : window.parent
  if (!controller || controller === window) return

  const post = (msg) => { try { controller.postMessage({ __pjqa: 1, ...msg }, '*') } catch { /* gone */ } }
  let busy = false

  async function runFlow(id, config) {
    const flow = FLOWS[id]
    if (!flow) { post({ type: 'run-end', ok: false, summary: `Unknown test "${id}"` }); return }
    post({ type: 'run-start', test: id, steps: flow.steps.map((s) => ({ title: s.title, hint: s.hint })) })
    let failedAt = -1
    for (let i = 0; i < flow.steps.length; i++) {
      post({ type: 'step', index: i, status: 'running' })
      const t0 = Date.now()
      try {
        const detail = await flow.steps[i].run(config)
        post({ type: 'step', index: i, status: 'done', detail: typeof detail === 'string' ? detail : undefined, ms: Date.now() - t0 })
      } catch (err) {
        post({ type: 'step', index: i, status: 'failed', error: (err && err.message) || String(err), ms: Date.now() - t0 })
        failedAt = i
        break
      }
    }
    post({
      type: 'run-end',
      ok: failedAt === -1,
      summary: failedAt === -1 ? `All ${flow.steps.length} steps passed` : `Failed at step ${failedAt + 1} — ${flow.steps[failedAt].title}`,
    })
  }

  window.addEventListener('message', (e) => {
    if (!isAllowedOrigin(e.origin)) return
    const d = e.data
    if (!d || !d.__pjqa) return
    if (d.type === 'run' && !busy) {
      busy = true
      Promise.resolve(runFlow(d.test, d.config || {})).finally(() => { busy = false })
    }
  })

  // Announce readiness (and re-announce shortly after, in case the controller
  // attaches its listener a beat later).
  const flows = Object.entries(FLOWS).map(([id, f]) => ({ id, title: f.title, outline: f.steps.map((s) => ({ title: s.title, hint: s.hint })) }))
  post({ type: 'ready', flows })
  setTimeout(() => post({ type: 'ready', flows }), 800)
}
